import { formatPublicSourceLabel } from "@/features/machines/addressing";
import type { DashboardWidget, ResolvedWidgetValue } from "../../types";
import { describeSignalDiagnostic } from "./diagnosticMeaning";
import { formatWidgetNumber, formatWidgetRelative } from "./format";
import { HeroCard, HeroMeaning, type HeroTone } from "./HeroCard";
import { describeQuality, effectiveQuality } from "./quality";

interface TankWidgetProps {
  widget: DashboardWidget;
  value: ResolvedWidgetValue;
  editable?: boolean;
  onConfigure?: () => void;
  onGenerateChart?: () => void;
}

/**
 * Fração de preenchimento entre `min` e `max`.
 *
 * O DESENHO é limitado a 0–100%, mas o VALOR exibido continua o real: um
 * sensor lendo acima da faixa mostra o número verdadeiro com o tanque cheio,
 * nunca um número recortado. Faixa degenerada (`max <= min`) devolve `null` —
 * sem escala não há fração honesta, e desenhar 0% seria inventar.
 */
export function tankFillFraction(
  value: number | null,
  min: number,
  max: number,
): number | null {
  if (value === null || !Number.isFinite(value)) return null;
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return null;
  return Math.min(1, Math.max(0, (value - min) / (max - min)));
}

/**
 * Tanque/nível: nível de líquido, altura de coluna, posição num curso.
 *
 * Só existe por escolha EXPLÍCITA do operador. Não nasce automaticamente
 * porque a unidade é `%` — nem toda porcentagem é um nível, e materializar um
 * tanque para uma umidade relativa seria o mesmo tipo de palpite que fazia
 * todo `v` virar "velocidade".
 *
 * A faixa vem do `/meta` quando o analógico a declara (`SignalConfig.scale`),
 * então o operador não redigita min/max que o configurador já conhece.
 *
 * Alarmes de baixo e alto reusam `settings.thresholds` com `direction`, que já
 * existiam — um tanque não precisa de um mecanismo próprio de limites.
 */
export function TankWidget({
  widget,
  value,
  editable,
  onConfigure,
  onGenerateChart,
}: TankWidgetProps) {
  const min = widget.settings.min ?? 0;
  const max = widget.settings.max ?? 100;
  const decimals = widget.settings.decimals ?? 1;
  const unit = widget.settings.unit?.trim() || null;

  const available = value.isAvailable;
  const fill = available ? tankFillFraction(value.numericValue, min, max) : null;
  const quality = describeQuality(effectiveQuality(value));
  const diagnosed = describeSignalDiagnostic(value.diagnostic);

  // Fora da faixa não é erro de leitura: é informação. O número real aparece e
  // o desenho satura, com uma frase dizendo o que aconteceu.
  const outOfRange =
    available && value.numericValue !== null
      ? value.numericValue > max
        ? "above"
        : value.numericValue < min
          ? "below"
          : null
      : null;

  // Faixa degenerada (`max <= min`): ha leitura, mas nao ha escala. Desenhar
  // 0% seria indistinguivel de um tanque legitimamente vazio — a mesma mentira
  // silenciosa que o estado "sem leitura" evita. Diz o que houve.
  const rangeBroken = available && value.numericValue !== null && fill === null;

  const meaning: { tone: HeroTone; text: string } | null =
    diagnosed ??
    (rangeBroken
      ? { tone: "warn", text: "Faixa inválida: máximo precisa ser maior que o mínimo" }
      : outOfRange === "above"
        ? { tone: "warn", text: "Acima do máximo da faixa" }
        : outOfRange === "below"
          ? { tone: "warn", text: "Abaixo do mínimo da faixa" }
          : null);

  const cardTone: "ok" | "warn" | "danger" =
    value.tone === "danger" || meaning?.tone === "danger"
      ? "danger"
      : value.tone === "warn" || meaning?.tone === "warn"
        ? "warn"
        : "ok";
  // Sem escala nao ha preenchimento: o corpo fica neutro em vez de fingir 0%.
  const drawsFill = fill !== null;

  const fillPercent = fill === null ? 0 : Math.round(fill * 100);
  const readout = available ? formatWidgetNumber(value.numericValue, decimals) : "--";

  return (
    <HeroCard
      title={widget.title}
      caption={formatPublicSourceLabel(widget.source, widget.settings.signalKind)}
      tone={cardTone}
      alarm={widget.settings.alarmEnabled}
      editable={editable}
      onConfigure={onConfigure}
      onGenerateChart={onGenerateChart}
      footnote={
        available
          ? `Atualizado ${formatWidgetRelative(value.measuredAt) ?? "agora"}`
          : quality.title
      }
    >
      <div className="w-hero__value">
        {readout}
        {unit && available ? <span className="w-hero__unit">{unit}</span> : null}
      </div>

      {meaning ? <HeroMeaning tone={meaning.tone}>{meaning.text}</HeroMeaning> : null}

      <div
        className="w-tank"
        role="img"
        aria-label={
        drawsFill
          ? `${widget.title}: ${readout}${unit ? ` ${unit}` : ""}, ${fillPercent}% da faixa`
          : `${widget.title}: ${readout}${unit ? ` ${unit}` : ""}, sem escala utilizável`
      }
      >
        <div className="w-tank__body">
          {drawsFill ? (
            <div
              className={
                cardTone === "danger"
                  ? "w-tank__fill w-tank__fill--danger"
                  : cardTone === "warn"
                    ? "w-tank__fill w-tank__fill--warn"
                    : "w-tank__fill"
              }
              style={{ height: `${fillPercent}%` }}
            />
          ) : null}
          {/* Um tanque desenhado em 0% seria indistinguível de "vazio de
              verdade", então nos dois casos sem preenchimento o corpo diz o
              motivo: sem leitura, ou sem escala utilizável. */}
          {!available ? (
            <span className="w-tank__empty">{quality.footerLabel}</span>
          ) : rangeBroken ? (
            <span className="w-tank__empty">SEM ESCALA</span>
          ) : null}
        </div>
        {/* A regua usa as MESMAS casas do readout. Com 0 fixo, um sensor de
            0–1,5 bar exibia "2" no topo: o numero dizia 1,4 e a regua dizia que
            o maximo era 2, sugerindo folga que nao existe. */}
        <div className="w-tank__scale">
          <span>{formatWidgetNumber(max, decimals)}</span>
          <span>{formatWidgetNumber(min, decimals)}</span>
        </div>
      </div>
    </HeroCard>
  );
}
